<!-- src/shared/components/modals/settings/JurisdictionList.vue -->

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import OIcon from '@/shared/components/icons/OIcon.vue';
import type { Jurisdiction } from '@/schemas/shapes/config';
import { resolveJurisdictionIcon, resolveJurisdictionDisplayName } from '@/shared/stores/jurisdictionStore';

const { t } = useI18n();

const props = defineProps<{
  jurisdictions: Jurisdiction[];
  currentJurisdiction?: Jurisdiction | null;
}>();

const isCurrentJurisdiction = (jurisdiction: Jurisdiction) =>
  jurisdiction.identifier === props.currentJurisdiction?.identifier;

const getIcon = (jurisdiction: Jurisdiction) => resolveJurisdictionIcon(jurisdiction);
const getDisplayName = (jurisdiction: Jurisdiction) => resolveJurisdictionDisplayName(jurisdiction, t);
</script>

<template>
  <ul
    class="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700"
    role="list">
    <li
      v-for="jurisdiction in jurisdictions"
      :key="jurisdiction.identifier"
      class="flex flex-wrap items-center gap-3 p-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 sm:flex-nowrap sm:p-4">
      <div class="flex w-full items-center gap-3 sm:w-auto">
        <OIcon
          :collection="getIcon(jurisdiction).collection"
          :name="getIcon(jurisdiction).name"
          class="size-5 shrink-0 text-gray-400 dark:text-gray-500"
          aria-hidden="true" />

        <a
          :href="`https://${jurisdiction.domain}/signup`"
          :class="{ 'font-medium': isCurrentJurisdiction(jurisdiction) }"
          class="grow text-sm text-gray-700 hover:text-brand-600 dark:text-gray-200 dark:hover:text-brand-400"
          :aria-current="isCurrentJurisdiction(jurisdiction) ? 'true' : undefined"
          :aria-label="t('web.regions.jurisdiction_display_name_iscurrentjurisdiction_', [getDisplayName(jurisdiction), isCurrentJurisdiction(jurisdiction) ? `(Current)` : ``])">
          {{ getDisplayName(jurisdiction) }}
        </a>
      </div>

      <span
        v-if="isCurrentJurisdiction(jurisdiction)"
        class="ml-auto inline-flex items-center rounded-full bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300"
        aria-hidden="true">
        {{ t('web.regions.current') }}
      </span>
    </li>
  </ul>
</template>
